import { HotkeyDecorator } from "@/components/atoms/HotkeyDecorator";
import { Icon } from "@/components/atoms/Icon";
import { HotkeyScope, useContextHotkeys } from "@/hooks/useContextTree";
import { useDevice } from "@/hooks/useDevice";
import { State } from "@/state";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import _ from "lodash";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeftIcon,
  Columns2Icon,
  MaximizeIcon,
  MinimizeIcon,
  Rows2Icon,
} from "lucide-react";

import { typographyVariants } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

export function CommandCenterTopbar() {
  const commandIdOfQuickActions = State(
    (state) => state.commandDefinitionIdOfQuickActions,
  );

  const activeCommandDefinition = State((state) =>
    state.commandDefinitions.find(
      (command) =>
        command.id ===
        state.commandInstanceStack[state.commandInstanceStack.length - 1]
          ?.commandDefinitionId,
    ),
  );
  const activeCommandInstance = State((state) =>
    state.commandInstanceStack.find(
      (commandInstance) =>
        commandInstance.commandDefinitionId === activeCommandDefinition?.id,
    ),
  );
  const dialogLayoutMode = State((state) => state.dialogLayoutMode);
  const dialogIsFullScreen = State((state) => state.dialogIsFullScreen);
  const isOnlyQuickActionsVisible = State(
    (state) =>
      state.commandInstanceStack.length === 1 &&
      state.commandInstanceStack[0].commandDefinitionId ===
        commandIdOfQuickActions,
  );
  const commandInstanceStack = State((state) => state.commandInstanceStack);
  const popCommandsFromStack = State((state) => state.popCommandsFromStack);

  // Pre-compute labels and store displayed stack for fade-out delay
  const [displayedStack, setDisplayedStack] = useState<
    Array<{
      commandInstance: (typeof commandInstanceStack)[0];
      label: string;
    }>
  >(() => {
    // Initialize with current stack and pre-computed labels
    return commandInstanceStack.map((commandInstance) => {
      const command = State.getState().commandDefinitions.find(
        (cmd) => cmd.id === commandInstance.commandDefinitionId,
      );
      const label =
        command?.getLabel?.(commandInstance.commandInstanceId) ||
        _.capitalize(commandInstance.commandInstanceId.split("-").join(" "));
      return { commandInstance, label };
    });
  });

  const debounceRef = useRef<ReturnType<typeof _.debounce> | null>(null);
  const previousStackLengthRef = useRef(commandInstanceStack.length);

  useEffect(() => {
    const previousLength = previousStackLengthRef.current;
    const currentLength = commandInstanceStack.length;

    // Update the ref for next comparison
    previousStackLengthRef.current = currentLength;

    // Cleanup any existing debounce timer
    if (debounceRef.current) {
      debounceRef.current.cancel();
      debounceRef.current = null;
    }

    // Only debounce when going from non-empty to empty
    if (previousLength > 0 && currentLength === 0) {
      // Create debounced function that updates displayed stack after 1 second
      debounceRef.current = _.debounce(() => {
        setDisplayedStack([]);
        debounceRef.current = null;
      }, 400);

      debounceRef.current();
    } else {
      // For all other cases, update immediately and pre-compute labels
      const newDisplayedStack = commandInstanceStack.map((commandInstance) => {
        const command = State.getState().commandDefinitions.find(
          (cmd) => cmd.id === commandInstance.commandDefinitionId,
        );
        const label =
          command?.getLabel?.(
            commandInstance.commandInstanceId,
            commandInstance.context,
          ) ||
          _.capitalize(commandInstance.commandInstanceId.split("-").join(" "));
        return { commandInstance, label };
      });
      setDisplayedStack(newDisplayedStack);
    }

    // Cleanup on unmount
    return () => {
      if (debounceRef.current) {
        debounceRef.current.cancel();
      }
    };
  }, [commandInstanceStack]);

  const isDisplayedStackOnlyQuickActions = useMemo(
    () =>
      displayedStack.length === 1 &&
      displayedStack[0].commandInstance.commandDefinitionId ===
        commandIdOfQuickActions,
    [displayedStack, commandIdOfQuickActions],
  );

  const context = useMemo(() => {
    if (activeCommandDefinition?.context) {
      return (
        <activeCommandDefinition.context
          commandInstanceId={activeCommandInstance.commandInstanceId}
          commandDefinitionId={activeCommandInstance.commandDefinitionId}
          context={activeCommandInstance?.context}
        />
      );
    }
    return null;
  }, [activeCommandInstance]);

  const dialogGroupCount = State((state) => state.dialogGroupCount);
  // Show layout toggle when the command supports split view and has more than one group
  // Neither on a phone, nor in a window as narrow: a dialog there is the whole
  // of it already, and too narrow for two columns side by side.
  const { isNarrow } = useDevice();
  const showLayoutToggle =
    !isNarrow &&
    activeCommandDefinition?.sizes?.includes("sm") &&
    dialogGroupCount > 1;
  // Show size toggle when the command supports fullscreen (has sizes with "lg")
  const showSizeToggle =
    !isNarrow && activeCommandDefinition?.sizes?.includes("lg");

  const toggleLayout = () => {
    const current = State.getState().dialogLayoutMode;
    State.setState({
      dialogLayoutMode: current === "split" ? "tabs" : "split",
    });
  };

  const toggleFullscreen = () => {
    const current = State.getState().dialogIsFullScreen;
    State.setState({ dialogIsFullScreen: !current });
  };

  // Hotkey: toggle layout mode (tabs <-> split)
  useContextHotkeys({
    id: "command-center-toggle-layout",
    combos: { mac: "l", windows: "l", linux: "l" },
    callback: toggleLayout,
    isEnabled: !!showLayoutToggle,
    scopeCheck: HotkeyScope.global,
  });

  // Hotkey: toggle fullscreen
  useContextHotkeys({
    id: "command-center-toggle-fullscreen",
    combos: { mac: "f", windows: "f", linux: "f" },
    callback: toggleFullscreen,
    isEnabled: !!showSizeToggle,
    scopeCheck: HotkeyScope.global,
  });

  return (
    <div className="flex flex-row items-center justify-between gap-1 w-full">
      {!isOnlyQuickActionsVisible && !isDisplayedStackOnlyQuickActions && (
        <div
          // gap: 0.5,
          className="flex flex-row items-center"
        >
          {displayedStack.length > 1 && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => {
                popCommandsFromStack();
              }}
              className="text-muted-foreground/80 hover:text-muted-foreground"
            >
              <Icon ReactIcon={ArrowLeftIcon} size="md" />
            </Button>
          )}
          {displayedStack.length > 0 && (
            <Breadcrumb>
              <BreadcrumbList>
                {displayedStack.map((item, index) => (
                  <Fragment key={item.commandInstance.commandInstanceId}>
                    {index > 0 && <BreadcrumbSeparator />}
                    <BreadcrumbItem>
                      {index === displayedStack.length - 1 ? (
                        <BreadcrumbPage>{item.label}</BreadcrumbPage>
                      ) : (
                        // Back to it: the commands opened over it go.
                        <BreadcrumbLink asChild>
                          <button
                            type="button"
                            className="cursor-pointer"
                            onClick={() =>
                              popCommandsFromStack(
                                displayedStack.length - index - 1,
                              )
                            }
                          >
                            {item.label}
                          </button>
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                  </Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          )}
        </div>
      )}
      {context}
      {(showLayoutToggle || showSizeToggle) && (
        <ButtonGroup>
          {/* Layout mode toggle */}
          {showLayoutToggle && (
            <Button
              variant="outline"
              size="sm"
              onClick={toggleLayout}
              className="pr-1"
            >
              <Icon
                ReactIcon={
                  dialogLayoutMode === "split" ? Rows2Icon : Columns2Icon
                }
                size="xs"
              />
              {dialogLayoutMode === "split" ? "Tab view" : "Split view"}
              <HotkeyDecorator hotkey={{ mac: "l", windows: "l" }} />
            </Button>
          )}
          {/* Size toggle */}
          {showSizeToggle && (
            <Button
              variant="outline"
              size="sm"
              onClick={toggleFullscreen}
              className="pr-1"
            >
              <Icon
                ReactIcon={dialogIsFullScreen ? MinimizeIcon : MaximizeIcon}
                size="xs"
              />
              {dialogIsFullScreen ? "Shrink" : "Expand"}
              <HotkeyDecorator hotkey={{ mac: "f", windows: "f" }} />
            </Button>
          )}
        </ButtonGroup>
      )}
    </div>
  );
}
